import { LinxStatus } from '@shared/constants/app-constants';
import { WorkflowStepErrorResponse } from '@shared/models';

import { CUSTOM_FIELD_UNRESOLVED, WORKFLOW_STATUS_INVALID } from './custom-field-errors';
import { CURRENCY_ERROR_CODES } from './debit-authorization-errors';
import { SIGN_HERE_RECOVERABLE_ERROR_CODES } from './sign-here-errors';

const WORKFLOW_NOT_FOUND = 'notfound';
const REQUEST_NOT_SUPPORTED = 'requestnotsupported';

/** What a failed API call leaves in Sentry (KOR-15194) */
export type ApiFailureReport = 'event' | 'breadcrumb' | 'none';

/**
 * The `errorCode`s a step acts on, so their failures are expected and not reported. A code the app starts handling
 * belongs here too, or every occurrence of it becomes a Sentry event.
 */
export const HANDLED_ERROR_CODES: ReadonlySet<string> = new Set([
  WORKFLOW_STATUS_INVALID,
  CUSTOM_FIELD_UNRESOLVED,
  // KOR-15254: a deleted or expired link. Workflow lookups (`LinxRequestComponent`, every step's `getCurrentStepDetails`)
  // and the linx-v1 form-data lookup send the customer to the inactive-request page, so the 404 is an expected ending.
  WORKFLOW_NOT_FOUND,
  // KOR-15254: a link for a request type the legacy linx-v1 flow cannot run; its landing pages show the inactive-request page.
  REQUEST_NOT_SUPPORTED,
  ...CURRENCY_ERROR_CODES,
  // KOR-15259: the backend already logs these with their cause (WARN, with trace ids); the sign-here step shows them.
  ...SIGN_HERE_RECOVERABLE_ERROR_CODES,
]);

/** Gateway, pod unavailable, timeout: the request never reached a service that could report it */
const GATEWAY_STATUSES = new Set([502, 503, 504]);

const asBody = (body: unknown): WorkflowStepErrorResponse => (body && typeof body === 'object' ? (body as WorkflowStepErrorResponse) : {});

/**
 * A plain 500 is meant to be reported by the backend with its cause, leaving only a breadcrumb here. No KOR service
 * reports to Sentry yet (KOR-15193 adds it), so until that is live in every environment the browser reports it too.
 * Then set this to false - KOR-15194 decision D4.
 */
const REPORT_SERVER_ERRORS_FROM_BROWSER = true;

/**
 * Which failed API calls the browser has to report itself: the ones the backend never sees (network, gateway), the
 * 4xx answers the app has no handling for, and for now a plain 500 (see above). Planned maintenance has its own page.
 */
export interface ApiFailureOptions {
  /** Report a plain 500 as an event (default: `REPORT_SERVER_ERRORS_FROM_BROWSER`) */
  reportServerErrors?: boolean;
  /**
   * Status 0 is what the browser reports when the page is going away or the device is offline - in volume, and not an
   * outage. With this set it is a breadcrumb; otherwise it is an event, so a real outage still is reported.
   */
  clientGone?: boolean;
  /**
   * The call is the browser's upload to a presigned storage URL, not to KOR. Its 4xx (an expired URL: 403) is shown to
   * the customer by the caller, and storage reports nothing, so it is a breadcrumb; a 5xx is still an event.
   */
  presignedUpload?: boolean;
}

const PRESIGNED_URL = /[&?]x-amz-signature=/iu;

/** A PUT to a presigned storage URL (the document upload), recognised by its signature parameter */
export const isPresignedUpload = (method: string, url: string): boolean => method.toUpperCase() === 'PUT' && PRESIGNED_URL.test(url);

export const classifyApiFailure = (
  status: number,
  body: unknown,
  { reportServerErrors = REPORT_SERVER_ERRORS_FROM_BROWSER, clientGone = false, presignedUpload = false }: ApiFailureOptions = {},
): ApiFailureReport => {
  if (body === LinxStatus.SYSTEM_MAINTENANCE) return 'breadcrumb';
  if (status === 0) return clientGone ? 'breadcrumb' : 'event';
  if (GATEWAY_STATUSES.has(status)) return 'event';
  if (status === 500) return reportServerErrors ? 'event' : 'breadcrumb';
  if (status < 400 || status >= 500) return 'none';
  if (presignedUpload) return 'breadcrumb';

  const { errorCode, errors } = asBody(body);
  if (errors?.length) return 'none';

  return errorCode && HANDLED_ERROR_CODES.has(errorCode) ? 'none' : 'event';
};
